import { useEffect, useState, useRef } from "react";

const sleep = (milliseconds: number) => {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
};

type PollingInstance<T> = {
  id: string;
  result: T | null;
  isPolling: boolean;
  stop: () => void;
};

export const useMultiPolling = <T,>({
  poll,
  checkResult = (result) => !!result,
  waitFor = 2000,
  maxRuns = 15,
}: {
  poll: (args?: any) => Promise<T>;
  checkResult?: (result: T, args?: any) => boolean;
  waitFor?: number;
  maxRuns?: number;
}) => {
  const [instances, setInstances] = useState<PollingInstance<T>[]>([]);
  const abortControllers = useRef<Map<string, AbortController>>(new Map());

  const stopInstance = (id: string) => {
    const controller = abortControllers.current.get(id);
    if (controller) {
      controller.abort();
      abortControllers.current.delete(id);
    }
    setInstances(prev => prev.filter(instance => instance.id !== id));
  };

  const start = async (args?: any) => {
    const id = Math.random().toString(36).substring(7);
    const controller = new AbortController();
    abortControllers.current.set(id, controller);

    setInstances(prev => [...prev, { id, result: null, isPolling: true, stop: () => stopInstance(id) }]);

    async function run(numberOfRuns = 0) {
      try {
        if (controller.signal.aborted) return;

        console.log(`🏊️ useMultiPolling - run ${numberOfRuns} for instance ${id}`);
        const _result = await poll(args);

        if (controller.signal.aborted) return;

        if (numberOfRuns >= maxRuns) {
          setInstances(prev => prev.filter(instance => instance.id !== id));
          return;
        }

        if (!checkResult(_result, args)) {
          await sleep(waitFor);
          if (controller.signal.aborted) return;
          return run(numberOfRuns + 1);
        }

        setInstances(prev => prev.map(instance => 
          instance.id === id 
            ? { ...instance, result: _result, isPolling: false }
            : instance
        ));
      } catch (error) {
        if (!controller.signal.aborted) {
          setInstances(prev => prev.filter(instance => instance.id !== id));
        }
      }
    }

    run();
    return id;
  };

  useEffect(() => {
    return () => {
      // Cleanup all instances on unmount
      abortControllers.current.forEach(controller => controller.abort());
      abortControllers.current.clear();
    };
  }, []);

  return {
    instances,
    start,
  };
}; 